var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React from 'react';
import { createUseStyles } from 'react-jss';
import { Text, RippleEffect } from '..';
import { useTheme } from '../../theme';
import { useMemoClasses } from '../../utils/hooks';
var useStyles = createUseStyles({
    primary: {
        '--RUI-button-main': 'var(--RUI-color-primary)',
        '--RUI-button-dark': 'var(--RUI-color-primary-dark)',
        '--RUI-button-light': 'var(--RUI-color-primary-light)',
        '--RUI-button-lighter': 'var(--RUI-color-primary-lighter)',
        '--RUI-button-contrast': 'var(--RUI-color-primary-contrast)',
    },
    secondary: {
        '--RUI-button-main': 'var(--RUI-color-secondary)',
        '--RUI-button-dark': 'var(--RUI-color-secondary-dark)',
        '--RUI-button-light': 'var(--RUI-color-secondary-light)',
        '--RUI-button-lighter': 'var(--RUI-color-secondary-lighter)',
        '--RUI-button-contrast': 'var(--RUI-color-secondary-contrast)',
    },
    neutral: {
        '--RUI-button-main': 'var(--RUI-color-neutral)',
        '--RUI-button-dark': 'var(--RUI-color-neutral-dark)',
        '--RUI-button-light': 'var(--RUI-color-neutral-light)',
        '--RUI-button-lighter': 'var(--RUI-color-neutral-lighter)',
        '--RUI-button-contrast': 'var(--RUI-color-neutral-contrast)',
    },
    error: {
        '--RUI-button-main': 'var(--RUI-color-error)',
        '--RUI-button-dark': 'var(--RUI-color-error-dark)',
        '--RUI-button-light': 'var(--RUI-color-error-light)',
        '--RUI-button-lighter': 'var(--RUI-color-error-lighter)',
        '--RUI-button-contrast': 'var(--RUI-color-error-contrast)',
    },
    warning: {
        '--RUI-button-main': 'var(--RUI-color-warning)',
        '--RUI-button-dark': 'var(--RUI-color-warning-dark)',
        '--RUI-button-light': 'var(--RUI-color-warning-light)',
        '--RUI-button-lighter': 'var(--RUI-color-warning-lighter)',
        '--RUI-button-contrast': 'var(--RUI-color-warning-contrast)',
    },
    success: {
        '--RUI-button-main': 'var(--RUI-color-success)',
        '--RUI-button-dark': 'var(--RUI-color-success-dark)',
        '--RUI-button-light': 'var(--RUI-color-success-light)',
        '--RUI-button-lighter': 'var(--RUI-color-success-lighter)',
        '--RUI-button-contrast': 'var(--RUI-color-success-contrast)',
    },
    base: {
        font: 'var(--RUI-font-button)',
        position: 'relative',
        cursor: 'pointer',
        padding: 'calc(0.7 * var(--RUI-padding)) var(--RUI-padding)',
        margin: 'var(--RUI-margin)',
        borderRadius: 'var(--RUI-border-radius)',
        border: 'none',
        boxSizing: 'border-box',
        width: 'fit-content',
        '-webkit-tap-highlight-color': 'transparent',
        '&:active, &:focus': {
            outline: 'none',
        },
        '&:disabled': {
            '--RUI-button-main': 'var(--RUI-color-neutral-light)',
            '--RUI-button-light': 'var(--RUI-color-neutral-lighter)',
            '--RUI-button-lighter': 'var(--RUI-color-neutral-lighter)',
            '--RUI-button-contrast': 'var(--RUI-color-neutral-contrast)',
            boxShadow: 'none !important',
            cursor: 'not-allowed',
        },
    },
    baseOverrides: function (theme) { return (__assign({}, theme.overrides.Button.base)); },
    text: {
        '--RUI-ripple-color': 'var(--RUI-button-dark)',
        color: 'var(--RUI-button-main)',
        backgroundColor: 'transparent',
        transition: 'background 0.2s',
        '&:hover:not(:disabled), &:focus': {
            backgroundColor: 'var(--RUI-button-lighter)',
        },
    },
    textOverrides: function (theme) { return (__assign({}, theme.overrides.Button.text)); },
    full: {
        '--RUI-ripple-color': 'var(--RUI-button-lighter)',
        color: 'var(--RUI-button-contrast)',
        backgroundColor: 'var(--RUI-button-main)',
        boxShadow: '2px 2px 6px -3px black',
        '&:hover:not(:disabled), &:focus': {
            backgroundColor: 'var(--RUI-button-light)',
        },
    },
    fullOverrides: function (theme) { return (__assign({}, theme.overrides.Button.full)); },
    outline: {
        '--RUI-ripple-color': 'var(--RUI-button-dark)',
        color: 'var(--RUI-button-main)',
        backgroundColor: 'transparent',
        padding: 'calc(0.7 * var(--RUI-padding) - var(--RUI-border-width)) calc(var(--RUI-padding) - var(--RUI-border-width))',
        border: "var(--RUI-border-width) solid var(--RUI-button-light)",
        '&:hover:not(:disabled), &:focus': {
            backgroundColor: 'var(--RUI-button-lighter)',
            borderColor: 'var(--RUI-button-main)',
        },
    },
    outlineOverrides: function (theme) { return (__assign({}, theme.overrides.Button.outline)); },
    fullWidth: {
        width: '100%',
    },
    fullWidthOverrides: function (theme) { return (__assign({}, theme.overrides.Button.fullWidth)); },
    big: { fontSize: '1.15em' },
    bigOverrides: function (theme) { return (__assign({}, theme.overrides.Button.big)); },
    small: { fontSize: '.65em' },
    smallOverrides: function (theme) { return (__assign({}, theme.overrides.Button.small)); },
}, { name: 'RUIButton' });
var Button = function (_a) {
    var children = _a.children, variant = _a.variant, color = _a.color, size = _a.size, type = _a.type, fullWidth = _a.fullWidth, ripple = _a.ripple, disabled = _a.disabled, className = _a.className, props = __rest(_a, ["children", "variant", "color", "size", "type", "fullWidth", "ripple", "disabled", "className"]);
    var theme = useTheme();
    var styles = useStyles(theme);
    var classes = useMemoClasses([
        styles.base,
        styles[color],
        theme.overrides.Button.base && styles.baseOverrides,
        size === 'small' && styles.small,
        size === 'small' && theme.overrides.Button.small && styles.smallOverrides,
        size === 'big' && styles.big,
        size === 'big' && theme.overrides.Button.big && styles.bigOverrides,
        fullWidth && styles.fullWidth,
        fullWidth && theme.overrides.Button.fullWidth && styles.fullWidthOverrides,
        styles[variant],
        variant === 'text' && theme.overrides.Button.text && styles.textOverrides,
        variant === 'full' && theme.overrides.Button.full && styles.fullOverrides,
        variant === 'outline' && theme.overrides.Button.outline && styles.outlineOverrides,
        'RUIButton-base',
        className,
    ], [theme, color, variant, size, fullWidth, className]);
    return (React.createElement("button", __assign({}, props, { type: type, className: classes, disabled: disabled }),
        React.createElement(Text, { variant: "button", color: "inherit" }, children),
        !disabled && ripple && React.createElement(RippleEffect, null)));
};
Button.defaultProps = {
    color: 'primary',
    variant: 'full',
    type: 'button',
    size: 'normal',
    fullWidth: false,
    className: '',
    children: null,
    disabled: false,
    ripple: true,
};
export { Button as default, Button };
